import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Card,
  CardContent
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

/**
 * QuickKnowledgeCheck Component
 * 
 * Displays a set of quiz questions with multiple choice answers
 * 
 * @param {Object} props
 * @param {Array} props.questions - Array of question objects with format:
 *   {
 *     question: "Question text",
 *     options: ["Option 1", "Option 2", ...],
 *     correctAnswer: "Option X", // The correct option
 *     explanation: "Explanation text"
 *   }
 */
const QuickKnowledgeCheck = ({ questions }) => {
  // State to track selected answers for each question
  const [selectedAnswers, setSelectedAnswers] = useState({});
  // State to track whether explanations should be shown (only for correct answers)
  const [showExplanations, setShowExplanations] = useState({});

  // Handle when an option is selected
  const handleOptionSelect = (questionIndex, option) => {
    // Set the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: option
    }));
    
    // Show the correct answer highlight whenever any selection is made
    setShowExplanations(prev => ({
      ...prev,
      [questionIndex]: true
    }));
  };

  // Check if an option is correct
  const isCorrectAnswer = (questionIndex, option) => {
    const question = questions[questionIndex];
    return option === question.correctAnswer;
  };

  // Get the state of an option (selected, correct, incorrect)
  const getOptionState = (questionIndex, option) => {
    const isSelected = selectedAnswers[questionIndex] === option;
    const isCorrect = isCorrectAnswer(questionIndex, option);
    const hasSelection = selectedAnswers[questionIndex] !== undefined;
    
    if (isSelected) {
      return isCorrect ? 'correct' : 'incorrect';
    } else if (hasSelection && isCorrect) {
      return 'highlight-correct';
    }
    return 'default';
  };

  return (
    <Box sx={{ mt: 4, mb: 6, width: '100%' }}>
      <Typography variant="h5" component="h2" sx={{ mb: 4, color: '#e57373', fontWeight: 600 }}>
        Quick Knowledge Check
      </Typography>
      
      <Grid container spacing={4}>
        {questions.map((question, qIndex) => (
          <Grid item xs={12} md={4} key={qIndex}>
            <Card 
              elevation={1} 
              sx={{ 
                width: '100%',
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                  Question {qIndex + 1}
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                  {question.question}
                </Typography>
                
                <List sx={{ mb: 2, flex: 1 }}>
                  {question.options.map((option, oIndex) => {
                    const optionState = getOptionState(qIndex, option);
                    const isSelected = selectedAnswers[qIndex] === option;
                    const isCorrect = option === question.correctAnswer;
                    
                    return (
                      <ListItem 
                        key={oIndex}
                        onClick={() => {
                          const previousSelection = selectedAnswers[qIndex];
                          const canSelect = !previousSelection || 
                            (option === question.correctAnswer && previousSelection !== question.correctAnswer);
                          
                          if (canSelect) {
                            handleOptionSelect(qIndex, option);
                          }
                        }}
                        sx={{
                          cursor: selectedAnswers[qIndex] === question.correctAnswer 
                            ? 'default' 
                            : option === question.correctAnswer 
                              ? 'pointer' 
                              : selectedAnswers[qIndex] 
                                ? 'default' 
                                : 'pointer',
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: optionState === 'correct' 
                            ? 'rgba(76, 175, 80, 0.1)' 
                            : optionState === 'incorrect' 
                              ? 'rgba(244, 67, 54, 0.1)'
                              : optionState === 'highlight-correct'
                                ? 'rgba(76, 175, 80, 0.05)'
                                : 'background.paper',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: (selectedAnswers[qIndex] === question.correctAnswer || 
                              (selectedAnswers[qIndex] && option !== question.correctAnswer))
                              ? undefined 
                              : 'action.hover'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {optionState === 'correct' ? (
                            <CheckCircleOutlineIcon color="success" />
                          ) : optionState === 'incorrect' ? (
                            <CheckCircleOutlineIcon color="error" />
                          ) : (
                            <RadioButtonUncheckedIcon color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={option} 
                          primaryTypographyProps={{
                            sx: { 
                              fontWeight: 
                                optionState === 'correct' || 
                                optionState === 'highlight-correct' 
                                  ? 500 
                                  : 400 
                            }
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
                
                {/* Only show explanation for correct answers */}
                {selectedAnswers[qIndex] === question.correctAnswer && (
                  <Box 
                    sx={{ 
                      p: 2,
                      bgcolor: 'rgba(76, 175, 80, 0.1)',
                      borderRadius: 1,
                      mt: 'auto'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Explanation:
                    </Typography>
                    <Typography variant="body2">
                      {question.explanation}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default QuickKnowledgeCheck; 